/**
 * Forgot-password flow. Two phases in one page:
 *   1. request — enter email; Cognito emails a reset code.
 *   2. confirm — enter the code + a new password.
 * On success, redirect to /signin with a confirmation banner.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

import { FormError } from "@/components/FormError";
import { doConfirmResetPassword, doResetPassword } from "@/lib/auth/useAuth";

const emailSchema = z.object({
  email: z.string().email("Enter a valid email address"),
});
type EmailForm = z.infer<typeof emailSchema>;

const resetSchema = z
  .object({
    code: z.string().min(1, "Enter the code from your email"),
    newPassword: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Confirm your new password"),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });
type ResetForm = z.infer<typeof resetSchema>;

export function ForgotPassword() {
  const [phase, setPhase] = useState<"request" | "confirm">("request");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const navigate = useNavigate();

  const emailForm = useForm<EmailForm>({ resolver: zodResolver(emailSchema) });
  const resetForm = useForm<ResetForm>({ resolver: zodResolver(resetSchema) });

  const onRequest = async (values: EmailForm) => {
    setSubmitting(true);
    setServerError(null);
    const result = await doResetPassword(values.email);
    setSubmitting(false);
    if (!result.ok) {
      setServerError(result.error ?? "Could not start the password reset");
      return;
    }
    setEmail(values.email);
    setPhase("confirm");
  };

  const onConfirm = async (values: ResetForm) => {
    setSubmitting(true);
    setServerError(null);
    const result = await doConfirmResetPassword({
      email,
      code: values.code,
      newPassword: values.newPassword,
    });
    setSubmitting(false);
    if (!result.ok) {
      setServerError(result.error ?? "Could not reset your password");
      return;
    }
    navigate("/signin", {
      replace: true,
      state: { notice: "Password updated. Sign in with your new password." },
    });
  };

  return (
    <div className="mx-auto max-w-md px-5 py-10 md:py-16">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Reset password
      </p>
      <h1 className="mb-3 font-serif text-3xl text-starz-navy md:text-4xl">
        {phase === "request" ? (
          <>
            Forgot your <em className="text-starz-gold">password</em>?
          </>
        ) : (
          <>
            Check your <em className="text-starz-gold">email</em>.
          </>
        )}
      </h1>
      <div className="mb-7 h-0.5 w-14 bg-starz-gold" aria-hidden="true" />

      {phase === "request" ? (
        <form onSubmit={emailForm.handleSubmit(onRequest)} className="space-y-4">
          <p className="text-sm text-warm-gray">
            Enter your email and we'll send a code to reset your password.
          </p>
          <Field label="Email" error={emailForm.formState.errors.email?.message}>
            <input
              type="email"
              autoComplete="email"
              {...emailForm.register("email")}
              className={inputClass(!!emailForm.formState.errors.email)}
            />
          </Field>
          <FormError message={serverError} />
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-starz-navy px-5 py-3 font-medium text-starz-off-white hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Send reset code"}
          </button>
        </form>
      ) : (
        <form onSubmit={resetForm.handleSubmit(onConfirm)} className="space-y-4">
          <p className="text-sm text-warm-gray">
            We sent a code to {email}. Enter it below with your new password.
          </p>
          <Field label="Reset code" error={resetForm.formState.errors.code?.message}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              {...resetForm.register("code")}
              className={inputClass(!!resetForm.formState.errors.code)}
            />
          </Field>
          <Field label="New password" error={resetForm.formState.errors.newPassword?.message}>
            <input
              type="password"
              autoComplete="new-password"
              {...resetForm.register("newPassword")}
              className={inputClass(!!resetForm.formState.errors.newPassword)}
            />
          </Field>
          <p className="-mt-2 text-xs text-warm-gray">
            At least 8 characters with upper &amp; lower case, a number, and a symbol.
          </p>
          <Field
            label="Confirm new password"
            error={resetForm.formState.errors.confirmPassword?.message}
          >
            <input
              type="password"
              autoComplete="new-password"
              {...resetForm.register("confirmPassword")}
              className={inputClass(!!resetForm.formState.errors.confirmPassword)}
            />
          </Field>
          <FormError message={serverError} />
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-starz-navy px-5 py-3 font-medium text-starz-off-white hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Updating…" : "Reset password"}
          </button>
          <button
            type="button"
            onClick={() => {
              setServerError(null);
              setPhase("request");
            }}
            className="w-full text-center text-sm text-warm-gray hover:text-starz-gold"
          >
            Use a different email
          </button>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-warm-gray">
        Remembered it?{" "}
        <Link to="/signin" className="font-medium text-starz-navy hover:text-starz-gold">
          Back to sign in
        </Link>
      </p>
    </div>
  );
}

function inputClass(hasError: boolean) {
  return [
    "w-full rounded-lg border bg-white px-4 py-2.5 text-starz-charcoal",
    "focus:outline-none focus:ring-2 focus:ring-starz-gold focus:ring-offset-2",
    hasError ? "border-starz-brick" : "border-starz-gold/25",
  ].join(" ");
}

interface FieldProps {
  label: string;
  error?: string;
  children: React.ReactNode;
}

function Field({ label, error, children }: FieldProps) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-warm-gray">
        {label} {error && <span className="text-starz-gold">*</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-starz-brick">{error}</span>}
    </label>
  );
}
